import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';

const STORAGE_KEYS = {
  currentStep: 'currentStep',
  selections: 'selections',
  navigationHistory: 'navigationHistory',
  summaryData: 'addict_summary_data',
};

function loadFromStorage(key, fallback) {
  try {
    const raw = localStorage.getItem(key);
    if (raw == null) return fallback;
    if (key === STORAGE_KEYS.selections || key === STORAGE_KEYS.navigationHistory) {
      return JSON.parse(raw);
    }
    return raw;
  } catch (e) {
    console.error(`SimulationContext: error loading ${key}`, e);
    return fallback;
  }
}

const SimulationContext = createContext(null);

/**
 * État global de la simulation : étape courante, sélections, historique.
 * Persistance localStorage et helpers pour le calcul du total.
 * Prêt pour une migration progressive de WizardContainer vers useSimulation().
 */
export function SimulationProvider({ children }) {
  const [currentStep, setCurrentStepState] = useState(() =>
    loadFromStorage(STORAGE_KEYS.currentStep, null)
  );
  const [selections, setSelectionsState] = useState(() =>
    loadFromStorage(STORAGE_KEYS.selections, {})
  );
  const [history, setHistoryState] = useState(() =>
    loadFromStorage(STORAGE_KEYS.navigationHistory, [])
  );

  // Persist to localStorage when state changes
  useEffect(() => {
    localStorage.setItem(STORAGE_KEYS.currentStep, currentStep);
  }, [currentStep]);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEYS.selections, JSON.stringify(selections));
  }, [selections]);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEYS.navigationHistory, JSON.stringify(history));
  }, [history]);

  const setCurrentStep = useCallback((valueOrUpdater) => {
    setCurrentStepState(valueOrUpdater);
  }, []);

  const setSelections = useCallback((updater) => {
    setSelectionsState((prev) => {
      const next = typeof updater === 'function' ? updater(prev) : updater;
      return next;
    });
  }, []);

  const setHistory = useCallback((updater) => {
    setHistoryState((prev) => {
      const next = typeof updater === 'function' ? updater(prev) : updater;
      return next;
    });
  }, []);

  /** Réinitialise la simulation et le storage (retour accueil). */
  const resetSimulation = useCallback(() => {
    setCurrentStepState(null);
    setSelectionsState({});
    setHistoryState([]);
    Object.values(STORAGE_KEYS).forEach((key) => localStorage.removeItem(key));
  }, []);

  const value = {
    currentStep,
    setCurrentStep,
    selections,
    setSelections,
    history,
    setHistory,
    resetSimulation,
    storageKeys: STORAGE_KEYS,
  };

  return (
    <SimulationContext.Provider value={value}>
      {children}
    </SimulationContext.Provider>
  );
}

export function useSimulation() {
  const ctx = useContext(SimulationContext);
  if (!ctx) {
    throw new Error('useSimulation must be used within SimulationProvider');
  }
  return ctx;
}

export { STORAGE_KEYS };
export default SimulationContext;
